import React from 'react';
import { cn } from '@/lib/utils';
import {
  User,
  Shield,
  Bell,
  Paintbrush,
  Code,
  LifeBuoy,
  Coins,
  Settings,
  Thermometer
} from 'lucide-react';

interface SettingsNavItem {
  id: string;
  icon: React.FC<{ className?: string }>;
  label: string;
  adminOnly?: boolean;
}

const settingsNavItems: SettingsNavItem[] = [
  { id: 'account', icon: User, label: 'Account' },
  { id: 'security', icon: Shield, label: 'Security' },
  { id: 'credits', icon: Coins, label: 'Credits' },
  { id: 'notifications', icon: Bell, label: 'Notifications' },
  { id: 'appearance', icon: Paintbrush, label: 'Appearance' },
  { id: 'api', icon: Code, label: 'API Keys' },
  { id: 'llm-controls', icon: Thermometer, label: 'LLM Controls', adminOnly: true },
  { id: 'about', icon: LifeBuoy, label: 'About' },
];

interface ModernSettingsLayoutProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  children: React.ReactNode;
  isAdmin?: boolean;
}

export const ModernSettingsLayout = ({
  activeTab,
  setActiveTab,
  children,
  isAdmin = false
}: ModernSettingsLayoutProps) => {
  
  // Filter nav items based on admin access
  const visibleNavItems = settingsNavItems.filter(item => !item.adminOnly || isAdmin);
  return (
    <div className="container mx-auto p-4 max-w-6xl min-h-screen">
      {/* Enhanced Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Dark theme background with gradient and pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl">
          <div className="absolute inset-0 bg-grid-slate-400/[0.05] bg-[size:20px_20px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5" />
        </div>
        
        {/* Content */}
        <div className="relative p-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-slate-700/80 backdrop-blur-sm border border-slate-600/50">
              <Settings className="h-7 w-7 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-100 to-slate-300 bg-clip-text text-transparent">
                Settings
              </h1>
              <p className="text-slate-400 text-lg mt-1">
                Manage your account settings and preferences
              </p>
            </div>
          </div>
          
          {/* Subtle accent line */}
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-60" />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 flex-shrink-0">
          <div className="bg-slate-700/50 rounded-lg shadow-sm border border-slate-600/50 overflow-hidden backdrop-blur-sm">
            <nav className="flex flex-col p-1">
              {visibleNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-left w-full transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                    activeTab === item.id 
                      ? "bg-blue-600/20 text-blue-300 font-medium" 
                      : "text-slate-300 hover:bg-slate-700/50 hover:text-slate-100"
                  )}
                >
                  <item.icon className={cn(
                    "h-5 w-5",
                    activeTab === item.id ? "text-blue-400" : "text-slate-400"
                  )} />
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          <div className="bg-slate-700/50 rounded-lg shadow-sm border border-slate-600/50 backdrop-blur-sm">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};